import { type MetaResponse, type PublicUser } from '@veriverse/shared';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api, logout } from '../net/api.js';
import { enterOffice, leaveOffice } from '../net/connection.js';
import { useSessionStore } from '../state/session-store.js';
import { prefersReducedMotion } from '../state/settings.js';
import { LazyAdminPanel } from './LazyAdminPanel.js';
import { AppTitle } from './AppTitle.js';
import { AppearanceEditor } from './AppearanceEditor.js';
import { CreditsLink } from './CreditsPanel.js';
import StarBorder from './react-bits/StarBorder.js';
import { SettingsPanel } from './SettingsPanel.js';

// Decorative React Bits effects load on demand so the shader stays out of the first download.
const LaserFlow = lazy(() => import('./react-bits/LaserFlow'));
const ElectricLogo = lazy(() => import('./react-bits/ElectricLogo'));

/** Between login and the world: enter the office, change avatar, admin console, sign out. */
export function Lobby({ user }: { user: PublicUser }) {
  const { t } = useTranslation();
  const status = useSessionStore((s) => s.status);
  const error = useSessionStore((s) => s.error);
  const [view, setView] = useState<'home' | 'avatar' | 'admin'>('home');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [offices, setOffices] = useState<readonly string[]>([]);
  const [picking, setPicking] = useState(false);
  const busy = status === 'connecting';
  const panelRef = useRef<HTMLElement>(null);

  useEffect(() => {
    // Without the list (or with a single office) "Enter the office" goes straight in.
    api<MetaResponse>('/api/meta')
      .then((meta) => setOffices(meta.offices))
      .catch(() => undefined);
  }, []);

  if (view === 'avatar') return <AppearanceEditor user={user} onDone={() => setView('home')} />;
  if (view === 'admin') return <LazyAdminPanel onClose={() => setView('home')} />;

  const signOut = () => {
    leaveOffice();
    void logout().then(() => useSessionStore.getState().setSignedOut());
  };

  return (
    <>
      <div className="join-laser" aria-hidden="true">
        <Suspense fallback={null}>
          <LaserFlow
            surfaceRef={panelRef}
            beamPosition={0.5}
            color="#d50101"
            intensity={1.45}
            flare={1.35}
            spread={1.5}
            spill={0.75}
            fog={0.35}
            dust={1.6}
            revealRadius={220}
            revealOpacity={0.55}
            streaks={1.65}
            dots={1.45}
            speed={0.75}
            seed={8}
          />
        </Suspense>
      </div>
      <div className="join-logo" aria-hidden="true">
        {prefersReducedMotion() ? (
          <img src="/logo.png" alt="" />
        ) : (
          <Suspense fallback={<img src="/logo.png" alt="" />}>
            <ElectricLogo
              src="/logo.png"
              color="#D5031A"
              glowColor="#0F151C"
              scale={0.95}
              strands={2.5}
              bend={0.22}
              crackle={3}
              arcs={2}
              speed={0.65}
              intensity={0.8}
              glow={0.45}
              thickness={1.0}
              flicker={0.35}
              fill={100}
              cursorIntensity={1.35}
              cursorRadius={245}
            />
          </Suspense>
        )}
      </div>
      <main ref={panelRef} className="panel join">
        <AppTitle />
        <p className="tagline">{t('lobby.welcome', { name: user.displayName })}</p>
        {error && (
          <p role="alert" className="error">
            {error.startsWith('errors.') ? t(error) : t('connection.error', { reason: error })}
          </p>
        )}
        {picking && !busy ? (
          <div className="stack" role="group" aria-label={t('lobby.pickOffice')}>
            <p className="tagline">{t('lobby.pickOffice')}</p>
            {offices.map((office, index) => (
              <button
                key={office}
                type="button"
                onClick={() => void enterOffice(office)}
                autoFocus={index === 0}
              >
                {office}
              </button>
            ))}
            <button type="button" className="secondary" onClick={() => setPicking(false)}>
              {t('lobby.back')}
            </button>
          </div>
        ) : (
          <div className="stack">
            <StarBorder
              color="#ffcf8a"
              trailColor="#ff4d2e"
              duration={5}
              direction="clockwise"
              stars={2}
              trailLength={0.35}
              thickness={2}
              radius={30}
              glow={0.8}
              hover="lap"
              clickPulse
              theme="dark"
              disabled={busy}
              onClick={() => (offices.length > 1 ? setPicking(true) : void enterOffice())}
            >
              {busy ? t('connection.connecting') : t('lobby.enter')}
            </StarBorder>
            <button type="button" className="secondary" onClick={() => setView('avatar')}>
              {t('lobby.editAvatar')}
            </button>
            {user.role === 'admin' && (
              <button type="button" className="secondary" onClick={() => setView('admin')}>
                {t('lobby.admin')}
              </button>
            )}
            <button type="button" className="secondary" onClick={() => setSettingsOpen(true)}>
              {t('settings.open')}
            </button>
            <button type="button" className="secondary" onClick={signOut}>
              {t('lobby.signOut')}
            </button>
          </div>
        )}
        <CreditsLink />
        {settingsOpen && <SettingsPanel onClose={() => setSettingsOpen(false)} />}
      </main>
    </>
  );
}
